import type { ClientAccount } from "../../clients/types/clients-api.types";
import { getBankOptionsForCurrency } from "../../confirmations/constants/paymentMethodsByCurrency.config";
import { getPaymentMethodFields } from "../constants/paymentMethodFields.config";
import type { PaymentMethodFields } from "../types/new-payment.types";

// BRL|Pix accounts are stored with the legacy keys `tipoLlavePix` (label) and
// `llavePix` (value), while the form fields use `pix_type` (code "0".."4") and
// `pix_key`. This maps the stored label back to the select code so the payout
// form can pre-fill it. Covers current and pre-existing label variants.
const PIX_TYPE_LABEL_TO_CODE: Record<string, string> = {
  CPF: "0",
  CNPJ: "1",
  Email: "2",
  EMAIL: "2",
  Teléfono: "3",
  EVP: "4",
  "Llave aleatoria": "4",
};

export function resolvePaymentBankAndCurrency(account: ClientAccount): {
  tipoMoneda: string;
  banco: string;
} {
  const cur = account.currency?.trim() ?? "";
  const bankName = account.bank_name?.trim() ?? "";
  const banks = getBankOptionsForCurrency(cur);
  const match = banks.find((o) => o.value === bankName || o.label === bankName);
  let banco = match?.value ?? bankName;
  if (cur === "ARS" && (banco === "CBU / CVU" || bankName === "CBU / CVU")) {
    banco = "CBU";
  }
  return {
    tipoMoneda: cur,
    banco,
  };
}

export function paymentMethodFieldsFromAccountMetadata(
  account: ClientAccount,
  tipoMoneda: string,
  banco: string,
): PaymentMethodFields {
  const fields = getPaymentMethodFields(tipoMoneda, banco);
  const meta = account.account_metadata ?? {};
  const out: PaymentMethodFields = {};
  for (const f of fields) {
    const raw = meta[f.id];
    if (raw !== undefined && raw !== null) {
      out[f.id] = String(raw);
    }
  }
  const legacyCbuCvu = meta.cbuCvu;
  if (
    tipoMoneda === "ARS" &&
    banco === "CBU" &&
    legacyCbuCvu != null &&
    legacyCbuCvu !== "" &&
    out.cbu === undefined
  ) {
    out.cbu = String(legacyCbuCvu);
  }
  if (tipoMoneda === "BRL" && banco === "Pix") {
    const legacyPixKey = meta.llavePix;
    if (out.pix_key === undefined && legacyPixKey != null && legacyPixKey !== "") {
      out.pix_key = String(legacyPixKey);
    }
    const legacyPixType = meta.tipoLlavePix;
    if (out.pix_type === undefined && legacyPixType != null && legacyPixType !== "") {
      const rawType = String(legacyPixType).trim();
      const code = PIX_TYPE_LABEL_TO_CODE[rawType] ?? (/^[0-4]$/.test(rawType) ? rawType : undefined);
      if (code !== undefined) out.pix_type = code;
    }
  }
  return out;
}
